import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKatonMeet } from './KatonMeetContext';
import { buildKatonMeetLobbyUrl, KATON_MEET_EVENTS, KATON_MEET_IFRAME_ALLOW, KATON_MEET_ORIGIN } from './katonMeetConfig';
import { getKatonMeetToken } from './katonMeetAuth';
import KatonMeetScreenPicker from './KatonMeetScreenPicker';
import Loader from '../Loader/Loader';
import ToasterWithMessage from '../toaster';
import { KATON_MEET_MESSAGES } from '../../utils/constants';
import './KatonMeetHost.scss';

const { info } = ToasterWithMessage();

const isDev = process.env.NODE_ENV === 'development';

interface DragState {
  startX: number;
  startY: number;
  originX: number;
  originY: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type KatonMeetMessage = { type: string; [key: string]: unknown };

// Messages can carry the user's token, so they only ever go to the Katon Meet origin.
// In development they are logged (with the token masked) to make the handshake easy to follow.
const postToMeet = (target: Window | null | undefined, message: KatonMeetMessage) => {
  if (!target || !KATON_MEET_ORIGIN) return;
  target.postMessage(message, KATON_MEET_ORIGIN);
  if (isDev) {
    console.debug(
      '[KatonMeet] sent',
      message.type,
      'accessToken' in message ? { ...message, accessToken: '***', ...('refreshToken' in message ? { refreshToken: '***' } : {}) } : message,
    );
  }
};

// Answers with a fresh access token and the Katon Meet meeting id (mId) to open,
// echoing Meet's nonce so it can match the reply to its request.
// Without a Classroom session nothing is sent, and Meet keeps its own sign-in.
const sendToken = (target: Window, mId?: string | null, nonce?: string) => {
  getKatonMeetToken()
    .then((token) => postToMeet(target, { type: KATON_MEET_EVENTS.AUTH, nonce, mId, ...token }))
    .catch((err) => {
      if (isDev) console.debug('[KatonMeet] no token to send', err);
    });
};

// Rendered once at the app root, outside <Routes>, so the iframe (and the call) survives page changes.
// Full and PiP modes only change CSS classes; the iframe element is never re-created or moved,
// because moving an iframe in the DOM reloads it and drops the call.
// There is no Classroom header: back (minimize), expand and leave are all done from Katon Meet's own UI.
// In PiP the only Classroom control is a small grip to drag the window around.
const KatonMeetHost = () => {
  const { session, mode, minimize, expand, closeMeeting } = useKatonMeet();
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Loader shown until the meeting page has loaded in the iframe
  const [isLoading, setIsLoading] = useState(true);
  const isPip = mode === 'pip';

  // Only changes when a different meeting is opened, so switching modes never reloads the iframe
  const src = useMemo(() => (session ? buildKatonMeetLobbyUrl(session.meetingId) : ''), [session]);

  // A newly opened meeting gets its own loader
  useEffect(() => {
    setIsLoading(true);
  }, [session]);

  // PiP position is applied straight to the element so dragging doesn't re-render the iframe
  const applyOffset = useCallback((x: number, y: number) => {
    offsetRef.current = { x, y };
    if (containerRef.current) {
      containerRef.current.style.transform = x || y ? `translate(${x}px, ${y}px)` : '';
    }
  }, []);

  // Each mode starts from its default position (PiP: bottom-right corner); a resize puts the PiP back there
  // so it can never end up off screen
  useEffect(() => {
    applyOffset(0, 0);
    if (!isPip) return;
    const handleResize = () => applyOffset(0, 0);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isPip, applyOffset]);

  // Tell Katon Meet which layout to render (full meeting UI or its PiP tile)
  useEffect(() => {
    postToMeet(frameRef.current?.contentWindow, { type: KATON_MEET_EVENTS.SET_PIP, on: isPip });
  }, [isPip]);

  useEffect(() => {
    if (!session) return;
    const handleMessage = (event: MessageEvent) => {
      // Only our own Katon Meet iframe may talk to us
      if (event.origin !== KATON_MEET_ORIGIN || event.source !== frameRef.current?.contentWindow) return;
      const target = event.source as Window;
      const nonce = typeof event.data?.nonce === 'string' ? event.data.nonce : undefined;
      if (isDev) console.debug('[KatonMeet] received', event.data?.type);
      switch (event.data?.type) {
        // Login handover: KM_AUTH is sent only in reply to Meet's KM_READY, which Meet sends once its
        // listener is attached (also after a reload inside the iframe). Every request gets an answer.
        case KATON_MEET_EVENTS.READY:
          sendToken(target, session?.meetingId, nonce);
          break;
        case KATON_MEET_EVENTS.SET_PIP:
          if (event.data?.on === true) minimize();
          else if (event.data?.on === false) expand();
          break;
        case KATON_MEET_EVENTS.LEFT:
        case KATON_MEET_EVENTS.CLOSE:
          closeMeeting();
          break;
        case KATON_MEET_EVENTS.ENDED:
          info(KATON_MEET_MESSAGES.hostEndedMeeting);
          closeMeeting();
          break;
        case KATON_MEET_EVENTS.MOBILE_SCREEN:
          info(KATON_MEET_MESSAGES.mobileNotSupported, 4000);
          closeMeeting();
          break;
        case KATON_MEET_EVENTS.HOST_KICKED:
          info(KATON_MEET_MESSAGES.removedByHost);
          closeMeeting();
          break;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [session, minimize, expand, closeMeeting]);

  // Refreshing or closing the tab ends the call, so ask first.
  // Skipped in Electron, where cancelling beforeunload silently blocks closing the window.
  useEffect(() => {
    if (!session || window?.electron?.isElectron?.()) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [session]);

  const handleDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: offsetRef.current.x,
      originY: offsetRef.current.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handleDragMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const container = containerRef.current;
    if (!drag || !container) return;
    const rect = container.getBoundingClientRect();
    // Position of the PiP without any drag offset, used to keep it inside the viewport
    const baseLeft = rect.left - offsetRef.current.x;
    const baseTop = rect.top - offsetRef.current.y;
    applyOffset(
      clamp(drag.originX + event.clientX - drag.startX, -baseLeft, window.innerWidth - rect.width - baseLeft),
      clamp(drag.originY + event.clientY - drag.startY, -baseTop, window.innerHeight - rect.height - baseTop),
    );
  };

  const handleDragEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
  };

  const handleFrameLoad = () => setIsLoading(false);

  if (!session) return null;

  return (
    <>
      <div
        ref={containerRef}
        className={`katonMeetHost ${isPip ? 'katonMeetHost--pip' : 'katonMeetHost--full'}`}
        role="dialog"
        aria-label="Live class meeting"
      >
        <iframe
          ref={frameRef}
          title="Live class"
          src={src}
          allow={KATON_MEET_IFRAME_ALLOW}
          allowFullScreen
          onLoad={handleFrameLoad}
          className={`katonMeetHost__frame${isDragging ? ' katonMeetHost__frame--dragging' : ''}`}
        />
        {isPip && (
          <div
            className={`katonMeetHost__dragHandle${isDragging ? ' katonMeetHost__dragHandle--dragging' : ''}`}
            title="Drag to move"
            aria-label="Move meeting window"
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
          />
        )}
        {isLoading && (
          <div className="katonMeetHost__loading" role="status" aria-live="polite">
            <div className="katonMeetHost__loader">
              <Loader />
            </div>
            {!isPip && (
              <>
                <p className="katonMeetHost__loadingTitle">{KATON_MEET_MESSAGES.joiningTitle}</p>
                <p className="katonMeetHost__loadingText">{KATON_MEET_MESSAGES.joiningText}</p>
              </>
            )}
          </div>
        )}
      </div>
      {/* Outside the container: the PiP box (small, overflow hidden) would otherwise clip this full-screen overlay */}
      <KatonMeetScreenPicker />
    </>
  );
};

export default KatonMeetHost;
